import {Trans} from 'react-i18next'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import makeStyles from '@material-ui/core/styles/makeStyles'
import {AlertDialog} from '../Dialog'
import messages from '../../../assets/messages'
import {getTauroTermsUrl} from '../TauroTermsCheckbox'

const useStyles = makeStyles((theme) => ({
  row: {
    padding: '6px 0',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  value: {
    fontWeight: 500,
    textAlign: 'right',
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
  terms: {
    paddingTop: 16,
  },
}))

/** The Tauro 10x factorisation config of the campaign the rules describe. */
export type TauroRules = {
  multiplier?: number
  drawdownTriggerPercent?: number
  tauroProfitSplitPercent?: number
  leverageDivisor?: number
  minQualifyingDeposit?: number
  maxQualifyingDeposit?: number
}

/** Base leverage divided by the campaign divisor, e.g. 10 → 1:50. */
const TAURO_BASE_LEVERAGE = 500

type TauroRulesModalProps = {
  open: boolean
  onClose: () => void
  rules: TauroRules | null
}

/**
 * The Tauro 10X Account Rules pop up (PT-5955): shows the factorisation terms of
 * the client's campaign before the account is created. Opened from the mandatory
 * acceptance checkbox in the Add Account flow; purely informational — the
 * acceptance itself stays in the checkbox.
 */
export const TauroRulesModal = ({open, onClose, rules}: TauroRulesModalProps) => {
  const classes = useStyles()
  const {
    multiplier,
    drawdownTriggerPercent,
    tauroProfitSplitPercent,
    leverageDivisor,
    minQualifyingDeposit,
    maxQualifyingDeposit,
  } = rules || {}

  const rows: Array<{key: string; label: {i18nKey: string; defaults: string}; values?: Record<string, unknown>; value: unknown}> = [
    {
      key: 'multiplier',
      label: messages.tauroRulesMultiplier,
      value: multiplier != null ? `${multiplier}x` : null,
    },
    {
      key: 'deposit-range',
      label: messages.tauroRulesQualifyingDeposit,
      value:
        minQualifyingDeposit != null && maxQualifyingDeposit != null
          ? `$${minQualifyingDeposit} – $${maxQualifyingDeposit}`
          : null,
    },
    {
      key: 'drawdown',
      label: messages.tauroRulesDrawdown,
      value: drawdownTriggerPercent != null ? `${drawdownTriggerPercent}%` : null,
    },
    {
      key: 'profit-split',
      label: messages.tauroRulesProfitSplit,
      value: tauroProfitSplitPercent != null ? `${100 - tauroProfitSplitPercent}% / ${tauroProfitSplitPercent}%` : null,
    },
    {
      key: 'leverage',
      label: messages.tauroRulesLeverage,
      value: leverageDivisor ? `1:${Math.round(TAURO_BASE_LEVERAGE / leverageDivisor)}` : null,
    },
  ]

  return (
    <AlertDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={<Trans {...messages.tauroRulesTitle} />}
      agreeText={<Trans {...messages.close} />}
      onAgree={onClose}
    >
      <Typography variant="body1" gutterBottom>
        <Trans {...messages.tauroRulesIntro} values={{multiplier: multiplier ?? 10}} />
      </Typography>
      <Grid container direction="column" id="tauroRulesModalRows">
        {rows
          .filter((row) => row.value != null)
          .map((row) => (
            <Grid item key={row.key} className={classes.row}>
              <Grid container justifyContent="space-between" wrap="nowrap">
                <Grid item>
                  <Typography variant="body2">
                    <Trans {...row.label} />
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2" className={classes.value}>
                    {String(row.value)}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ))}
      </Grid>
      <Typography variant="body2" className={classes.terms}>
        <Trans
          {...messages.tauroRulesFullTerms}
          components={[
            <a
              className={classes.link}
              href={getTauroTermsUrl()}
              rel="noreferrer noopener"
              target="_blank"
            >
              terms
            </a>,
          ]}
        />
      </Typography>
    </AlertDialog>
  )
}
